/**
 * Types for the Cart API
 * Matches backend CartViewModel / CartLineViewModel
 */

import type { ProductVariant, StockLevel } from "@/types/product";

/**
 * Whether a product ships or is delivered as an entitlement. Computed server-side
 * (one rule, in the backend's `ProductDeliveryType`) and carried on both cart lines
 * and finalised order lines, so analytics can split hardware from subscription
 * revenue without the frontend inferring it.
 */
export type DeliveryType = "physical" | "digital";

export interface CartLineAttribute {
  id: string;
  type: string;
  key: string;
  keyName: string;
  value: string;
  valueName: string;
}

export interface CartLineImage {
  id: string;
  type: string;
  url: string;
  name: string;
  width: number;
  height: number;
  blurString: string;
  altText: string;
}

export interface CartLineLink {
  id: string;
  type: string;
  url: string;
  culture: string;
  target: string | null;
  name: string;
}

export interface CartLine {
  id: string;
  productReference: string;
  variantReference: string;
  sku: string;
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  totalTax: number;
  unitPriceWithoutAdjustments: number;
  totalPriceWithoutAdjustments: number;
  formattedUnitPrice: string | null;
  formattedTotalPrice: string | null;
  formattedTotalTax: string | null;
  formattedUnitPriceWithoutAdjustments: string | null;
  formattedTotalPriceWithoutAdjustments: string | null;
  /**
   * Standard (pre-discount / list) unit price INCLUDING VAT. For B2B carts the
   * retailer breakdown shows this as the "Recommended price" above the net price.
   * Null when unavailable.
   */
  formattedStandardUnitPriceWithVat: string | null;
  image: CartLineImage | null;
  productLink: CartLineLink | null;
  variantAttributes: CartLineAttribute[];
  /** IMEI of the device this subscription is bound to. Null for non-subscription lines. */
  deviceImei: string | null;
  /** Human-friendly device name shown next to the IMEI in the cart UI. */
  deviceName: string | null;
  /**
   * Product type from the underlying CMS product (e.g. "app-license",
   * "device-subscription"). Null when the product has no productType set.
   * Used to suppress the quantity picker for one-per-line product types.
   */
  productType: string | null;
  /**
   * Whether this line ships. Reported to the dataLayer as the item's
   * `item_delivery_type`. Resolved server-side so the frontend never re-derives
   * it from `productType` — see the backend's ProductDeliveryType.
   */
  deliveryType: DeliveryType;
  /**
   * Formatted VAT rate for this line (e.g. "25 %"). Null for B2B (tax-excluded)
   * carts or lines with no tax. Used by the subscription checkout to show an
   * "incl. X% VAT" breakdown.
   */
  vatRatePercentage: string | null;
  /**
   * Groups the lines added together as one bundle; null for a standalone line.
   * Unique per add, so the same bundle bought twice yields two groups.
   */
  bundleId: string | null;
  /** Document id of the bundle this line came from. Null for a standalone line. */
  bundleProductId: string | null;
  /**
   * Display name of the bundle this line belongs to. Null for a standalone line,
   * or when the bundle document no longer exists.
   */
  bundleName: string | null;
  /**
   * The product is sold in versions (size, colour, ...) but this line names none,
   * so the customer still owes a choice and checkout is blocked until they make
   * it. Reached by adding a bundle from a product card, which has no room for one
   * picker per bundled item.
   *
   * Optional because older cached responses may not carry it — treat missing as
   * false, i.e. nothing to resolve.
   */
  requiresVariantSelection?: boolean;
  /**
   * Every version of this line's product, for the cart's picker. Absent for a
   * product sold in a single version, which is most of them.
   */
  availableVariants?: ProductVariant[] | null;
  /**
   * Availability band of what this line buys — the chosen version's for a
   * product sold in versions. Read from current stock each time the cart is
   * built, never stamped when the line was added. Null when none may be shown
   * (hidden for this audience, never synced, digital, or no version chosen).
   * Information only: an out-of-stock line can still be checked out.
   *
   * Optional because older cached responses may not carry it — treat missing as
   * null, i.e. show nothing.
   */
  stockLevel?: StockLevel | null;
}

/**
 * The lines still owing a version, in cart order. Checkout is blocked while this
 * is non-empty, and the first entry is what the page scrolls to.
 */
export function linesNeedingVariant(lines: readonly CartLine[]): CartLine[] {
  return lines.filter((line) => line.requiresVariantSelection === true);
}

/**
 * The lines belonging to one bundle add, or a single standalone line. Removing or
 * re-quantifying ANY line of a bundle applies to the whole group server-side —
 * the bundle discount only holds while every item is present.
 */
export interface CartLineGroup {
  /** Bundle id, or the line id for a standalone line. Stable React key. */
  key: string;
  /** Bundle name when this is a bundle group, else null. */
  bundleName: string | null;
  lines: CartLine[];
}

/**
 * Splits cart lines into bundle groups, preserving the server's line order and
 * keeping each bundle's lines adjacent (a bundle's lines are contiguous as added,
 * but a later add of an unrelated product must not be pulled into the group).
 */
export function groupCartLines(lines: readonly CartLine[]): CartLineGroup[] {
  const groups: CartLineGroup[] = [];
  const byBundleId = new Map<string, CartLineGroup>();

  for (const line of lines) {
    if (!line.bundleId) {
      groups.push({ key: line.id, bundleName: null, lines: [line] });
      continue;
    }

    const existing = byBundleId.get(line.bundleId);
    if (existing) {
      existing.lines.push(line);
      continue;
    }

    const group: CartLineGroup = {
      key: line.bundleId,
      bundleName: line.bundleName,
      lines: [line],
    };
    byBundleId.set(line.bundleId, group);
    groups.push(group);
  }

  return groups;
}

/** True when the line costs nothing — shown as "Free" rather than "0,00 €". */
export function isFreeLine(line: CartLine): boolean {
  return line.totalPrice === 0;
}

/** Recurring billing cadence of a subscription cart. */
export interface BillingPeriod {
  /** Raw unit: "day" | "week" | "month" | "year". */
  interval: string;
  /** Units per billing cycle (e.g. interval "month" + frequency 3 = quarterly). */
  frequency: number | null;
}

/** Product types that are bought one-per-line — quantity picker is hidden in the cart UI. */
export const FIXED_QUANTITY_PRODUCT_TYPES: readonly string[] = [
  "app-license",
  "device-subscription",
];

export function isFixedQuantityProductType(
  productType: string | null | undefined
): boolean {
  return (
    productType != null && FIXED_QUANTITY_PRODUCT_TYPES.includes(productType)
  );
}

export interface ShippingInfo {
  fullName: string | null;
  companyName: string | null;
  address: string | null;
  zipCode: string | null;
  city: string | null;
}

export interface SuggestedProduct {
  id: string;
  type: string;
  [key: string]: unknown;
}

// Values are camelCase because the backend serialises enums via
// JsonStringEnumConverter(JsonNamingPolicy.CamelCase).
export type CheckoutMode =
  | "empty"
  | "physical"
  | "digitalOneTime"
  | "digitalRecurring";

/**
 * Cart-level metadata describing a membership plan change. Present when the
 * user clicked Upgrade/Downgrade on the Change Plan view; the cart UI uses it
 * to render "Plan change: X → Y", the prorated amount, and the correct
 * checkout button (Confirm plan change vs. Continue to payment).
 */
export interface MembershipPlanChange {
  newTierIdentifier: string;
  /** Identifier of the current (about-to-be-replaced) tier; null for free users. */
  currentTierIdentifier: string | null;
  /** Existing Stripe subscription id; null for free users. */
  existingSubscriptionId: string | null;
  /** True when the user already has a paid sub — use Confirm Plan Change instead of Stripe Checkout. */
  isPaidUser: boolean;
  /**
   * ISO instant of the first payment when the member's remaining PROMOTIONAL grant
   * (support-granted days, a redeemed offline licence) is honoured by DEFERRING that payment
   * rather than discounting it. Null on every other cart.
   *
   * The line keeps its full price — a Stripe trial changes the first charge, not the
   * subscription's recurring amount — so the cart has to say this explicitly. Otherwise it
   * shows a full amount due today and the payment step then asks for zero.
   *
   * Optional because older cached responses may not carry it — treat missing as null.
   */
  grantTrialEndUtc?: string | null;
  /**
   * Value of that remaining grant in minor units when it is honoured as MONEY instead — a
   * different tier, or a remainder too long to defer. Null otherwise.
   *
   * The reduction already reaches the cart total as a line price adjustment; this exists so the
   * cart can NAME it. Without it the member sees an unexplained discount with no way to tell it
   * is the granted time they were promised.
   *
   * Optional because older cached responses may not carry it — treat missing as null.
   */
  grantCreditMinor?: number | null;
  /** Lowercase ISO currency of `grantCreditMinor`; null when there is no credit. */
  grantCreditCurrency?: string | null;
  /** Prorated amount due today, in minor units. Null for free users. */
  prorationAmountMinor: number | null;
  /** Lowercase ISO currency code for the prorated amount. */
  prorationCurrency: string | null;
  /**
   * What the remaining period on the new plan costs before credit, in minor
   * units. Always set together with `prorationCreditMinor`; both null when the
   * change couldn't be priced, in which case the UI shows only the amount due.
   */
  prorationChargeMinor: number | null;
  /**
   * Credit (positive, minor units) for the unused time on the plan being
   * replaced. `prorationChargeMinor − prorationCreditMinor === prorationAmountMinor`.
   */
  prorationCreditMinor: number | null;
  /**
   * VAT contained in `prorationAmountMinor`, in minor units, as Stripe computed
   * it for the member's country (OSS). Null when the preview carried no tax
   * breakdown — show no VAT line at all rather than implying zero VAT.
   */
  prorationTaxMinor: number | null;
  /**
   * Effective VAT rate of the amount due, as a percentage (e.g. 25). Null when
   * several rates apply; the VAT amount is then shown without a rate.
   */
  prorationTaxRatePercent: number | null;
  /**
   * "always_invoice" (upgrade — charged today) or "create_prorations"
   * (downgrade — credited against the next invoice). Null for free users.
   */
  prorationBehavior: string | null;
  /**
   * ISO timestamp of the existing subscription's renewal date. A plan change
   * never moves it — the cart says so explicitly. Null for free users.
   */
  renewalDate: string | null;
  /** Display name of the tier being switched to. Null when the CMS tier has no name. */
  newTierName: string | null;
  /** Display name of the tier being replaced. Null when the current Stripe price matches no CMS tier. */
  currentTierName: string | null;
  /**
   * True when this purchase replaces a subscription in a LEGACY Stripe account:
   * the user pays full price now and the old subscription is cancelled + the
   * unused time refunded as cash after checkout. Drives the cart banner.
   */
  isLegacyMigration: boolean;
  /** Estimated unused-time refund in minor units (legacy migration only). */
  legacyRefundEstimateMinor: number | null;
  /** Lowercase ISO currency of the legacy refund — may differ from the cart currency. */
  legacyRefundCurrency: string | null;
}

/**
 * Cart-level metadata for a GPS MAX migration cart (seeded by
 * POST /cart/gps-max-migration): the GPS MAX N purchase whose first payment is
 * discounted by the unused-time credit of the subscriptions being replaced.
 * The credit is already baked into the cart totals server-side — the UI rows
 * are informational.
 */
export interface GpsMaxCartMigration {
  /** Device count the purchased GPS MAX tier option covers. */
  targetDeviceCount: number;
  /** How many existing subscriptions will be cancelled after purchase. */
  subscriptionsToCancelCount: number;
  /** Credit applied as a first-payment discount, minor units (positive). */
  creditAppliedMinor: number;
  /** Lowercase ISO currency of the credit. */
  currency: string;
}

/**
 * Who provides the service and who legally sells it — the latter being the Stripe
 * account holder whose name lands on the member's card statement. Both are
 * configured per site server-side; the object is absent when either is missing,
 * so the UI never renders half a disclosure.
 */
export interface CartMerchant {
  serviceProviderName: string;
  sellerName: string;
}

/**
 * The free-delivery threshold a B2B cart is measured against, and the copy authored either
 * side of it. Both messages come from the checkout page in Umbraco rather than the dictionary,
 * so editors keep one copy of the wording for both storefronts; either can be null when the
 * current culture has none authored.
 *
 * Informational only — nothing here discounts the real shipping charge, and the B2B checkout
 * shows no delivery row at all.
 */
export interface FreeDelivery {
  /** Order value at or above which delivery is free, in the cart's currency. */
  threshold: number;
  /** `threshold` formatted with the currency, substituted into either message's `{threshold}`. */
  formattedThreshold: string;
  aboveThresholdMessage: string | null;
  belowThresholdMessage: string | null;
}

export interface Cart {
  lines: CartLine[];
  currency: string | null;
  notes: string | null;
  purchaser: string | null;
  externalOrderReference: string | null;
  totalPrice: string | null;
  subtotalPrice: string | null;
  subtotalPriceWithoutAdjustments: string | null;
  priceWithoutAdjustments: string | null;
  deliveryPrice: string | null;
  tax: string | null;
  discountCodes: string[];
  giftCards: string[];
  shippingInfo: ShippingInfo | null;
  suggestedProducts: SuggestedProduct[];
  mode: CheckoutMode;
  /**
   * Whether the cart as a whole ships — physical as soon as any line does. Null for
   * an empty cart. Reported as the funnel events' `delivery_type`.
   *
   * Related to `mode` but a different question: `mode` picks the checkout flow (and
   * so counts a deep-link product as physical, because it is bought through the
   * shipping-capable invoice cart), while this says whether anything is posted.
   */
  deliveryType: DeliveryType | null;
  /**
   * True when the current member is a B2B customer. Prices are shown net (VAT 0%)
   * and the cart UI hides consumer-only affordances (promo code, payment icons).
   */
  isB2b: boolean;
  /**
   * True when the B2B customer is blocked in Business Central: they keep full read access to the
   * shop, but cannot place orders. Always false for consumers.
   *
   * Display state only — the backend refuses the add-to-cart and the place-order calls itself, and
   * answers 403 with `errorCode: "b2b_account_blocked"` if the UI is out of date.
   */
  isB2bBlocked: boolean;
  /**
   * Free-delivery threshold and copy for the B2B checkout nudge. Null for a consumer cart,
   * and for a site whose checkout page names no threshold in this cart's currency.
   */
  freeDelivery: FreeDelivery | null;
  /** Service provider + legal seller for the pre-payment disclosure. Null when unconfigured for the site. */
  merchant: CartMerchant | null;
  /** Present when the cart represents a membership plan change. */
  membershipChange: MembershipPlanChange | null;
  /** Present when the cart was seeded by the GPS MAX migration flow. */
  gpsMaxMigration: GpsMaxCartMigration | null;
  /**
   * Recurring billing period for a subscription cart (digitalRecurring). Null for
   * physical / one-time / empty carts. Drives the "/ year" suffix on the price.
   */
  billingPeriod: BillingPeriod | null;
}

/** Request body for POST /api/v2/cart/line */
export interface UpdateLineRequest {
  orderLineId: string;
  quantity: number;
}

/**
 * Request body for POST /api/v2/cart/line/variant. The backend replaces the line
 * (Commerce cannot change a line's variant in place), carrying its quantity and
 * bundle properties over.
 */
export interface SetLineVariantRequest {
  orderLineId: string;
  variantId: string;
}

/** Request body for POST /api/v2/cart (add product) */
export interface AddToCartRequest {
  productId: string;
  variantId?: string;
  quantity: number;
}

/**
 * Request body for POST /api/v2/cart/bundle. The bundle itself has no SKU: the
 * backend explodes it into one order line per included product, all stamped with
 * a shared bundle id.
 */
export interface AddBundleToCartRequest {
  bundleProductId: string;
  quantity: number;
  /** Chosen variant per included product, for bundle items sold in versions. */
  variants?: { productId: string; variantId: string }[];
}

/** Request body for POST|DELETE /api/v2/cart/coupon */
export interface CouponRequest {
  code: string;
}
